import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, ArrowRight, Printer, Bookmark, ChevronLeft, ChevronRight, X, Gift, FileText } from "lucide-react";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

// Slugs surfaced as "reach for first" — only rendered when we have enough of them in the library.
const FEATURED_SLUGS = ["performance-issue", "big-life-events"];
const FEATURED_MIN = 3; // hide the row until at least this many featured scenarios are published

// Category display order — canonical taxonomy (must match backend CATEGORIES).
const CATEGORY_ORDER = [
  "Managing Performance & Accountability",
  "Emotional & Human Moments",
  "Team Dynamics & Relationships",
  "Communication & Transparency",
  "Identity, Authority & Leadership Presence",
  "Career & Transitions",
  "Managing Up & Organizational Reality",
  "Self-Management for Managers",
  "Managing Change",
];

// One-line editorial framing displayed at the top of each category section in the Library.
// Keep these short and operational — the user's voice, not therapy language.
const CATEGORY_INTROS = {
  "Managing Performance & Accountability": "These are about holding standards without losing humanity.",
  "Emotional & Human Moments": "These are about being human at work.",
};

// Brand color per category — used as the left-edge tab on each scenario card.
// Strictly from the Manage(Meant) Excellence palette (teal, plum, sage,
// coral, gold, gray). Cards visually differentiate from the blog without
// breaking brand family. Tuples below: [edge color, very-soft tint for the
// faint background wash on hover].
const CATEGORY_ACCENT = {
  "Managing Performance & Accountability": { edge: "var(--mme-teal)",     wash: "rgba(8, 114, 128, 0.045)" },
  "Emotional & Human Moments":              { edge: "var(--mme-plum)",     wash: "rgba(136, 98, 119, 0.045)" },
  "Team Dynamics & Relationships":          { edge: "var(--mme-sage)",     wash: "rgba(74, 158, 139, 0.045)" },
  "Communication & Transparency":           { edge: "var(--mme-teal-soft)",wash: "rgba(63, 142, 153, 0.045)" },
  "Identity, Authority & Leadership Presence": { edge: "var(--mme-coral)", wash: "rgba(244, 125, 95, 0.045)" },
  "Career & Transitions":                   { edge: "#1F4D5A",             wash: "rgba(31, 77, 90, 0.045)" },
  "Managing Up & Organizational Reality":   { edge: "var(--mme-gray)",     wash: "rgba(108, 109, 112, 0.045)" },
  "Self-Management for Managers":           { edge: "var(--mme-plum-soft)",wash: "rgba(168, 133, 151, 0.045)" },
  "Managing Change":                        { edge: "var(--mme-sage-deep)",wash: "rgba(53, 122, 107, 0.045)" },
};
const DEFAULT_ACCENT = { edge: "var(--mme-teal)", wash: "rgba(8, 114, 128, 0.045)" };

const fadeUp = {
  hidden: { opacity: 0, y: 8 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.04, duration: 0.5, ease: [0.22, 1, 0.36, 1] },
  }),
};

// Slugify a category for use as an anchor id (e.g. "Emotional & Human Moments" → "cat-emotional-human-moments").
function categoryAnchorId(category) {
  return `cat-${category.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`;
}

function LibraryTOC({ grouped, sticky = false }) {
  if (grouped.length <= 1) return null;
  return (
    <nav
      className={
        sticky
          ? "pl-8 border-l"
          : "mb-12 pb-8 border-b"
      }
      style={{ borderColor: "var(--mek-border)" }}
      aria-label="Library table of contents"
      data-testid={sticky ? "library-toc-sticky" : "library-toc"}
    >
      <p
        className="text-[10px] font-bold uppercase tracking-[0.22em] mb-4"
        style={{ color: "var(--mek-text-3)" }}
      >
        Jump to
      </p>
      <ul className={sticky ? "space-y-3" : "flex flex-wrap gap-x-6 gap-y-2"}>
        {grouped.map(([category, items]) => {
          const id = categoryAnchorId(category);
          return (
            <li key={category}>
              <a
                href={`#${id}`}
                onClick={(e) => {
                  e.preventDefault();
                  const el = document.getElementById(id);
                  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className={
                  sticky
                    ? "block text-sm leading-snug transition-colors hover:text-[color:var(--mme-teal)]"
                    : "text-sm font-medium inline-flex items-baseline gap-1.5 transition-colors hover:text-[color:var(--mme-teal)]"
                }
                style={{ color: "var(--mek-text)" }}
                data-testid={`library-toc-${id}`}
              >
                <span className={sticky ? "font-medium" : ""}>{category}</span>
                <span
                  className={sticky ? "ml-1.5 text-[10px] font-normal" : "text-[10px] font-normal"}
                  style={{ color: "var(--mek-text-3)" }}
                >
                  {items.length}
                </span>
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

function ScenarioCard({ s, i, variant = "default" }) {
  const isFeatured = variant === "featured";
  const accent = CATEGORY_ACCENT[s.category] || DEFAULT_ACCENT;
  return (
    <motion.div
      variants={fadeUp}
      custom={i}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-60px" }}
      className="h-full"
    >
      <Link
        to={`/library/${s.slug}`}
        className="block h-full group relative overflow-hidden transition-all duration-200 hover:-translate-y-0.5"
        data-testid={`library-scenario-${s.slug}`}
        style={{
          background: "#FBF6E8",
          border: "1px solid var(--mek-border)",
          borderRadius: "10px",
          minHeight: isFeatured ? "320px" : "360px",
          paddingTop: "28px",
          paddingBottom: "28px",
          paddingLeft: "30px",
          paddingRight: "26px",
        }}
      >
        {/* Left-edge category color rule */}
        <span
          aria-hidden="true"
          className="absolute left-0 top-0 bottom-0"
          style={{ width: "5px", background: accent.edge }}
        />
        {/* Soft category wash that fades in on hover */}
        <span
          aria-hidden="true"
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
          style={{ background: accent.wash }}
        />
        <div className="relative flex flex-col h-full">
          <p
            className="text-[10px] font-bold uppercase tracking-[0.22em] mb-4"
            style={{ color: accent.edge }}
          >
            {isFeatured ? "Reach for first" : s.category}
          </p>
          <p
            className="font-serif text-[26px] md:text-[28px] leading-[1.15] mb-4"
            style={{ color: "var(--mek-text)" }}
          >
            {s.title}
          </p>
          <p
            className="text-prose text-[15px] leading-relaxed mb-6"
            style={{ color: "var(--mek-text-2)" }}
          >
            {s.tagline}
          </p>
          <span
            className="mt-auto text-xs font-semibold inline-flex items-center gap-1.5 transition-transform group-hover:translate-x-0.5 uppercase tracking-[0.14em]"
            style={{ color: accent.edge }}
          >
            Open the kit <ArrowRight size={12} strokeWidth={2.5} />
          </span>
        </div>
      </Link>
    </motion.div>
  );
}

// Horizontal-scroll row of ScenarioCards for a single category. Replaces
// the old multi-row grid so a category with 7 scenarios is one tidy strip
// instead of three vertical rows. Includes left/right pager arrows that
// appear only when there is something to scroll to, soft fade gradients
// on the edges to hint at off-screen cards, and CSS scroll-snap so cards
// settle cleanly. Cards stay the same field-card design — only the
// container layout changes.
function CategoryRow({ items, accent }) {
  const scrollerRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const updateArrows = () => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    updateArrows();
    const el = scrollerRef.current;
    if (!el) return undefined;
    el.addEventListener("scroll", updateArrows, { passive: true });
    window.addEventListener("resize", updateArrows);
    return () => {
      el.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, [items.length]);

  // Scroll by ~one card width + gap so each click advances by a single card.
  const step = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const cardEl = el.querySelector("[data-card]");
    const cardW = cardEl ? cardEl.offsetWidth + 20 : el.clientWidth * 0.4;
    el.scrollBy({ left: dir * cardW, behavior: "smooth" });
  };

  return (
    <div className="relative">
      {/* Left fade + arrow */}
      <button
        type="button"
        aria-label="Scroll left"
        onClick={() => step(-1)}
        className={`hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 items-center justify-center w-10 h-10 rounded-full transition-all duration-200 ${
          canLeft ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        style={{
          background: "#ffffff",
          border: `1px solid ${accent.edge}`,
          color: accent.edge,
          boxShadow: "0 6px 20px rgba(31, 85, 89, 0.10)",
          marginLeft: "-14px",
        }}
        data-testid="category-scroll-left"
      >
        <ChevronLeft size={18} strokeWidth={2.4} />
      </button>

      {/* Right fade + arrow */}
      <button
        type="button"
        aria-label="Scroll right"
        onClick={() => step(1)}
        className={`hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-10 items-center justify-center w-10 h-10 rounded-full transition-all duration-200 ${
          canRight ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        style={{
          background: "#ffffff",
          border: `1px solid ${accent.edge}`,
          color: accent.edge,
          boxShadow: "0 6px 20px rgba(31, 85, 89, 0.10)",
          marginRight: "-14px",
        }}
        data-testid="category-scroll-right"
      >
        <ChevronRight size={18} strokeWidth={2.4} />
      </button>

      <div
        ref={scrollerRef}
        className="flex gap-5 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-2 -mx-2 px-2"
        style={{ scrollbarWidth: "thin" }}
      >
        {items.map((s, i) => (
          <div
            key={s.slug}
            data-card
            className="shrink-0 snap-start w-[88%] sm:w-[46%] lg:w-[31%] xl:w-[30%]"
          >
            <ScenarioCard s={s} i={i} />
          </div>
        ))}
      </div>
    </div>
  );
}


export default function Library() {
  const [scenarios, setScenarios] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [feedbackDismissed, setFeedbackDismissed] = useState(() => {
    try {
      return localStorage.getItem("mek.feedbackOfferDismissed") === "1";
    } catch {
      return false;
    }
  });
  const auth = useAuth();
  const navigate = useNavigate();

  const dismissFeedback = () => {
    setFeedbackDismissed(true);
    try {
      localStorage.setItem("mek.feedbackOfferDismissed", "1");
    } catch {
      /* ignore quota errors */
    }
  };

  useEffect(() => {
    if (auth.loading) return;
    if (!auth.authenticated) {
      navigate("/access");
      return;
    }
    api
      .get("/library/scenarios")
      .then((r) => setScenarios(r.data))
      .catch(() => navigate("/access"))
      .finally(() => setLoading(false));
  }, [auth, navigate]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return scenarios;
    return scenarios.filter((s) => {
      return (
        s.title.toLowerCase().includes(q) ||
        s.tagline.toLowerCase().includes(q) ||
        s.preview.toLowerCase().includes(q) ||
        s.category.toLowerCase().includes(q)
      );
    });
  }, [scenarios, query]);

  const featured = useMemo(() => {
    if (query.trim()) return [];
    const found = FEATURED_SLUGS.map((slug) => scenarios.find((s) => s.slug === slug)).filter(Boolean);
    return found.length >= FEATURED_MIN ? found : [];
  }, [scenarios, query]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const s of filtered) {
      if (!map.has(s.category)) map.set(s.category, []);
      map.get(s.category).push(s);
    }
    // Order categories by editorial preference, with anything new appended at the end.
    const ordered = [];
    for (const cat of CATEGORY_ORDER) {
      if (map.has(cat)) {
        ordered.push([cat, map.get(cat)]);
        map.delete(cat);
      }
    }
    for (const [cat, items] of map.entries()) ordered.push([cat, items]);
    return ordered;
  }, [filtered]);

  const userName = auth?.email?.split("@")[0] || null;

  // Most recent scenario update across the toolkit — used for the
  // "Last updated" maintenance badge in the welcome eyebrow.
  const lastUpdated = useMemo(() => {
    let max = null;
    for (const s of scenarios) {
      const t = s.updated_at;
      if (t && (!max || t > max)) max = t;
    }
    if (!max) return null;
    try {
      const d = new Date(max);
      return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    } catch {
      return null;
    }
  }, [scenarios]);

  return (
    <div className="App" data-testid="library-page">
      <Nav />

      {/* Welcome header */}
      <section className="pt-12 md:pt-20 pb-12 md:pb-16">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <p
            className="text-xs font-bold uppercase tracking-[0.22em] mb-5"
            style={{ color: "var(--mme-teal)" }}
          >
            {userName ? `Welcome back, ${userName}` : "Your toolkit"}
          </p>
          <h1 className="h-display text-4xl md:text-6xl mb-5 leading-[1.1]" data-testid="library-headline">
            {scenarios.length > 0 ? (
              <>
                {scenarios.length} {scenarios.length === 1 ? "scenario" : "scenarios"},{" "}
                <em className="italic">organized for the moments they're needed.</em>
              </>
            ) : (
              <>
                Your toolkit, <em className="italic">organized for the moments they're needed.</em>
              </>
            )}
          </h1>
          <p className="text-prose text-lg max-w-2xl">
            Open the one that fits today. Each scenario is built to be skimmed in two minutes — or printed and brought into the conversation with you. The library is being added to, section by section.
          </p>
          {lastUpdated && (
            <div
              className="inline-flex items-center gap-2 mt-6 px-3.5 py-1.5 rounded-full text-xs"
              style={{
                background: "var(--mek-bg-2)",
                border: "1px solid var(--mek-border)",
                color: "var(--mek-text-2)",
              }}
              data-testid="library-last-updated"
              title="The toolkit is actively maintained — this is the most recent edit across all scenarios."
            >
              <span
                aria-hidden="true"
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: "var(--mme-teal)" }}
              />
              <span>Toolkit maintained · last updated {lastUpdated}</span>
            </div>
          )}
        </div>
      </section>

      {/* Feedback offer (dismissible, library-only) */}
      {!feedbackDismissed && (
        <section className="pb-8" data-testid="feedback-offer-section">
          <div className="max-w-6xl mx-auto px-6 md:px-12">
            <div
              className="relative px-5 py-5 md:px-6 md:py-5 rounded-lg flex items-start gap-4"
              style={{
                background: "#FBF6E8",
                border: "1px solid var(--mme-gold-deep, #C9A95A)",
              }}
              data-testid="feedback-offer-card"
            >
              <span
                aria-hidden="true"
                className="shrink-0 inline-flex items-center justify-center rounded-full"
                style={{
                  width: 38,
                  height: 38,
                  background: "rgba(201, 169, 90, 0.18)",
                  color: "var(--mme-gold-deep, #8A6B1F)",
                }}
              >
                <Gift size={18} strokeWidth={1.8} />
              </span>
              <div className="flex-1 min-w-0">
                <p
                  className="text-[10px] font-bold uppercase tracking-[0.18em] mb-1.5"
                  style={{ color: "var(--mme-gold-deep, #8A6B1F)" }}
                >
                  A small thank-you
                </p>
                <p
                  className="text-sm md:text-base leading-snug mb-3"
                  style={{ color: "var(--mek-text)" }}
                >
                  Share what's working (or not) in the toolkit and I'll send you the{" "}
                  <span className="font-semibold">Manager Tools: Executive Presentation Starter Kit</span>{" "}
                  as a thank-you.
                </p>
                <a
                  href="mailto:hello@managemeantexcellence.com?subject=Manager%20Toolkit%20feedback&body=Hi%20Courtney%2C%0A%0AHere's%20my%20feedback%20on%20the%20Manager%20Toolkit%3A%0A%0A%0A%0A--%0A%0A(Send%20this%20email%20to%20receive%20the%20Executive%20Presentation%20Starter%20Kit%20as%20a%20thank-you!)"
                  className="inline-flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-full transition-opacity hover:opacity-90"
                  style={{ background: "var(--mme-teal)", color: "var(--mek-bg)" }}
                  data-testid="feedback-offer-cta"
                >
                  Share feedback <ArrowRight size={14} />
                </a>
              </div>
              <button
                type="button"
                onClick={dismissFeedback}
                className="shrink-0 inline-flex items-center justify-center rounded-full hover:opacity-70 transition-opacity"
                style={{ width: 28, height: 28, color: "var(--mek-text-3)" }}
                aria-label="Dismiss feedback offer"
                data-testid="feedback-offer-dismiss"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Toolbar */}
      <section className="pb-12">
        <div className="max-w-6xl mx-auto px-6 md:px-12 flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
          <div
            className="flex items-center gap-3 px-5 py-3 rounded-full flex-1 max-w-md"
            style={{ background: "var(--mek-bg-2)", border: "1px solid var(--mek-border)" }}
          >
            <Search size={16} style={{ color: "var(--mek-text-3)" }} />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by situation, emotion, or word…"
              className="bg-transparent flex-1 outline-none text-sm"
              data-testid="library-search-input"
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                className="text-xs"
                style={{ color: "var(--mek-text-3)" }}
                data-testid="library-search-clear"
              >
                Clear
              </button>
            )}
          </div>
          <div className="flex items-center gap-5 text-sm">
            <button
              onClick={() => window.print()}
              className="flex items-center gap-2 text-prose hover:text-[color:var(--mme-teal)]"
              data-testid="library-print-btn"
            >
              <Printer size={14} /> Print this index
            </button>
            <button
              onClick={async () => {
                try {
                  const res = await api.get("/library/toolkit.pdf", { responseType: "blob" });
                  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = "manager-toolkit.pdf";
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
                } catch (e) {
                  alert("We couldn't generate the PDF right now. Please try again in a moment.");
                }
              }}
              className="flex items-center gap-2 text-prose hover:text-[color:var(--mme-teal)]"
              data-testid="library-download-btn"
              title="Download a printable PDF of the full toolkit"
            >
              <Bookmark size={14} /> Download full kit (PDF)
            </button>
            {auth?.has_paid_purchase && (
              <button
                onClick={async () => {
                  try {
                    const res = await api.get("/library/receipt.pdf", { responseType: "blob" });
                    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = "manager-toolkit-receipt.pdf";
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
                  } catch (e) {
                    alert("We couldn't generate the receipt right now. Please try again or email hello@managemeantexcellence.com.");
                  }
                }}
                className="flex items-center gap-2 text-prose hover:text-[color:var(--mme-teal)]"
                data-testid="library-receipt-btn"
                title="Download a one-page PDF receipt for expense reports"
              >
                <FileText size={14} /> Receipt
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Featured row */}
      {featured.length > 0 && (
        <section className="pb-16 md:pb-20" data-testid="featured-row">
          <div className="max-w-6xl mx-auto px-6 md:px-12">
            <div className="flex items-baseline justify-between mb-6">
              <h2 className="font-serif text-2xl md:text-[28px]" style={{ color: "var(--mek-text)" }}>
                Most managers reach for these <em className="italic">on a normal hard day.</em>
              </h2>
              <p className="hidden md:block text-prose text-sm">
                The everyday weight of the role.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
              {featured.map((s, i) => (
                <ScenarioCard key={s.slug} s={s} i={i} variant="featured" />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Full library grouped by category */}
      <section className="pb-32" data-testid="library-grouped">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          {!loading && filtered.length === 0 && (
            <div className="text-center py-20">
              <p className="font-serif text-2xl mb-3">No scenarios match "{query}".</p>
              <p className="text-prose">Try a different word, or clear the search.</p>
              <button onClick={() => setQuery("")} className="btn-secondary mt-6">Clear search</button>
            </div>
          )}

          {!query.trim() && (
            <div className="flex items-baseline justify-between mb-10">
              <h2 className="font-serif text-2xl md:text-[28px]" style={{ color: "var(--mek-text)" }}>
                The full library.
              </h2>
              <p className="text-prose text-sm">
                {scenarios.length} scenarios · grouped by moment
              </p>
            </div>
          )}

          <div className="lg:flex lg:gap-12 lg:items-start">
            <div className="lg:flex-1 lg:min-w-0">
              {!query.trim() && (
                <div className="lg:hidden">
                  <LibraryTOC grouped={grouped} />
                </div>
              )}

              {grouped.map(([category, items]) => {
                const id = categoryAnchorId(category);
                const catAccent = CATEGORY_ACCENT[category] || DEFAULT_ACCENT;
                return (
                <div key={category} id={id} className="mb-14 last:mb-0 scroll-mt-24">
                  <div className="flex items-baseline justify-between mb-3">
                    <p
                      className="text-[15px] font-bold uppercase tracking-[0.18em]"
                      style={{ color: catAccent.edge }}
                    >
                      {category}
                    </p>
                    <p className="text-xs text-prose">
                      {items.length} {items.length === 1 ? "scenario" : "scenarios"}
                </p>
              </div>
              {CATEGORY_INTROS[category] && (
                <p
                  className="font-serif italic text-lg md:text-xl mb-6 max-w-2xl leading-snug"
                  style={{ color: "var(--mek-text-2)" }}
                  data-testid={`category-intro-${category}`}
                >
                  {CATEGORY_INTROS[category]}
                </p>
              )}
              <CategoryRow items={items} accent={catAccent} />
            </div>
          );})}
            </div>

            {!query.trim() && grouped.length > 1 && (
              <aside
                className="hidden lg:block lg:w-60 lg:shrink-0 lg:sticky lg:top-28 lg:self-start"
              >
                <LibraryTOC grouped={grouped} sticky />
              </aside>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
